import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Alert, Box, CircularProgress, IconButton, Snackbar, Tooltip } from '@mui/material';
import { DataGrid, type GridColDef, type GridPaginationModel, type GridSortModel } from '@mui/x-data-grid';
import type { StrapiCustomerRow, StrapiQuotationRow, StrapiQuotationUserStub } from '../api/strapiCrm';
import { formatCompanyBranchLabel } from '../api/strapiCrm';
import type { StrapiUser } from '../api/strapi';
import { incompleteCustomerFieldLabels } from '../pages/CustomersPage/customerCompleteness';
import { quotationGrandTotalFromRow } from '../pages/QuotationsPage/quotationGrandTotal';
import {
  canShowCreateWorkOrder,
  canCopyQuotation,
  canShowPicCustomerApprove,
  canShowViewSamplingSchedules,
  canShowEditInGrid,
  canShowQuotationPdfPreview,
  canShowQuotationWorkflowUi,
  isMarketingManajer,
  isMarketingPic,
  isMarketingSpv,
  quotationAwaitingCustomerApprove,
  quotationIsNeedAttention,
  quotationRowNeedsWorkflowFinancialDetail,
  quotationStatusDisplayLabel,
  resolveQuotationTahapan,
  resolveQuotationTypeLabel,
  type QuotationWorkflowGrandTotalOpts,
} from '../pages/QuotationsPage/quotationWorkflow';
import { QuotationWorkflowActions } from '../pages/QuotationsPage/QuotationWorkflowActions';
import { useQuotationDetailQuery } from '../query/hooks/useQuotationDetailQuery';
import { DataGridNumberedPagination, DATA_GRID_NUMBERED_PAGE_SIZE_OPTIONS } from './DataGridNumberedPagination';

function formatDate(s?: string | null): string {
  if (!s) return '';
  const dayOnly = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (dayOnly) {
    const d = new Date(Number(dayOnly[1]), Number(dayOnly[2]) - 1, Number(dayOnly[3]));
    return d.toLocaleDateString('id-ID');
  }
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString('id-ID');
}

function formatDateTime(s?: string | null): string {
  if (!s) return '';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString('id-ID', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatUserStub(u?: StrapiQuotationUserStub | null): string {
  if (!u) return '';
  const name = u.username?.trim();
  if (name) return name;
  return u.email?.trim() ?? '';
}

type QuotationCustomerGap = {
  title: string;
  labels: string[];
};

function customerGap(
  title: string,
  customer: StrapiCustomerRow | null | undefined,
): QuotationCustomerGap | null {
  if (!customer) return { title, labels: ['Pelanggan'] };
  const labels = incompleteCustomerFieldLabels(customer, 'approve');
  return labels.length > 0 ? { title, labels } : null;
}

/** Pelanggan tiap grup sampel konsultan. Customer yang sama di beberapa sampel digabung. */
function consultantSampleCustomerGaps(row: StrapiQuotationRow): QuotationCustomerGap[] {
  const groups = row.quotation_groups ?? [];
  const buckets: Array<{ samples: number[]; customer: StrapiCustomerRow | null; key: string }> = [];

  groups.forEach((group, index) => {
    const sample = index + 1;
    const id = group.customer?.documentId?.trim() ?? '';
    if (id) {
      const existing = buckets.find((bucket) => bucket.key === id);
      if (existing) {
        existing.samples.push(sample);
        return;
      }
    }
    buckets.push({
      samples: [sample],
      customer: group.customer ?? null,
      key: id || `missing-${sample}`,
    });
  });

  const gaps: QuotationCustomerGap[] = [];
  for (const bucket of buckets) {
    const name = bucket.customer?.name?.trim() || null;
    const prefix = bucket.samples.map((n) => `Sampel ${n}`).join(', ');
    const gap = customerGap(name ? `${prefix} — ${name}` : prefix, bucket.customer);
    if (gap) gaps.push(gap);
  }

  const seenIds = new Set(buckets.map((bucket) => bucket.key));
  const headerId = row.customer?.documentId?.trim() ?? '';
  if (!headerId || !seenIds.has(headerId)) {
    const name = row.customer?.name?.trim();
    const gap = customerGap(name ? `Pelanggan quotation — ${name}` : 'Pelanggan quotation', row.customer);
    if (gap) gaps.push(gap);
  }
  return gaps;
}

function quotationCustomerGaps(row: StrapiQuotationRow): QuotationCustomerGap[] {
  if (row.is_consultant_quote && (row.quotation_groups?.length ?? 0) > 0) {
    return consultantSampleCustomerGaps(row);
  }
  const name = row.customer?.name?.trim();
  const gap = customerGap(name || 'Pelanggan', row.customer);
  return gap ? [gap] : [];
}

function formatQuotationCustomerGaps(gaps: QuotationCustomerGap[], perSample: boolean): string {
  if (!perSample) {
    return `Data pelanggan Wajib diisi sbb:\n${gaps[0]?.labels.map((label) => `• ${label}`).join('\n') ?? ''}`;
  }
  return gaps
    .map((gap) => `${gap.title}\nData pelanggan Wajib diisi sbb:\n${gap.labels.map((label) => `• ${label}`).join('\n')}`)
    .join('\n\n');
}

/** PIC (penanggung jawab), SPV marketing, dan manajer marketing. */
function canSeeQuotationCustomerGaps(
  row: StrapiQuotationRow,
  user: StrapiUser | null,
  opts?: QuotationWorkflowGrandTotalOpts,
): boolean {
  if (isMarketingSpv(user) || isMarketingManajer(user)) {
    return quotationAwaitingCustomerApprove(row, opts);
  }
  return isMarketingPic(user) && canShowPicCustomerApprove(row, user, opts);
}

/** Reminder saat tombol setujui pelanggan sudah tampil dan data pelanggan masih kosong. */
function quotationPicCustomerGaps(
  row: StrapiQuotationRow,
  user: StrapiUser | null,
  opts?: QuotationWorkflowGrandTotalOpts,
): QuotationCustomerGap[] {
  if (!canSeeQuotationCustomerGaps(row, user, opts)) return [];
  return quotationCustomerGaps(row);
}

function QuotationCustomerIncompleteHint({
  row,
  user,
  jwt,
  onIncompleteChange,
}: {
  row: StrapiQuotationRow;
  user: StrapiUser | null;
  jwt: string | null;
  onIncompleteChange: (documentId: string, incomplete: boolean) => void;
}) {
  const track = canSeeQuotationCustomerGaps(row, user);
  const needsFinancialDetail = track && quotationRowNeedsWorkflowFinancialDetail(row.progress_status);
  const financialDetailQ = useQuotationDetailQuery(jwt, needsFinancialDetail ? row.documentId : null);
  const grandTotal = useMemo(() => {
    if (!track) return undefined;
    if (needsFinancialDetail && financialDetailQ.isLoading && !financialDetailQ.data) return null;
    return quotationGrandTotalFromRow(financialDetailQ.data ?? row);
  }, [track, needsFinancialDetail, financialDetailQ.isLoading, financialDetailQ.data, row]);
  const detailRow = financialDetailQ.data ?? null;
  const waitingForConsultantGroups =
    row.is_consultant_quote && needsFinancialDetail && financialDetailQ.isLoading && !detailRow;
  const customerRow: StrapiQuotationRow = detailRow
    ? { ...row, customer: detailRow.customer, quotation_groups: detailRow.quotation_groups }
    : row;
  const gaps = waitingForConsultantGroups
    ? []
    : quotationPicCustomerGaps(customerRow, user, track ? { grandTotal } : undefined);
  const incomplete = gaps.length > 0;
  const perSample = customerRow.is_consultant_quote && (customerRow.quotation_groups?.length ?? 0) > 0;

  useEffect(() => {
    onIncompleteChange(row.documentId, incomplete);
  }, [incomplete, onIncompleteChange, row.documentId]);

  if (!incomplete) return null;
  const customerName = row.customer?.name?.trim() || row.quotation_number;
  return (
    <Tooltip
      title={formatQuotationCustomerGaps(gaps, perSample)}
      slotProps={{ tooltip: { sx: { maxWidth: 420, whiteSpace: 'pre-line' } } }}
    >
      <IconButton
        size="small"
        color="warning"
        aria-label={`Data ${customerName} belum lengkap`}
        data-testid={`quotation-action-customer-incomplete-${row.documentId}`}
      >
        <InfoOutlinedIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}

type QuotationDataGridProps = {
  rows: StrapiQuotationRow[];
  rowCount: number;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  sortModel: GridSortModel;
  onSortModelChange: (model: GridSortModel) => void;
  isLoading: boolean;
  isError: boolean;
  error?: Error | null;
  user: StrapiUser | null;
  jwt: string | null;
  onViewDetail: (row: StrapiQuotationRow) => void;
  onPreview: (row: StrapiQuotationRow) => void;
  onPreviewQuotationPdf: (row: StrapiQuotationRow) => void;
  pdfRegenerateFeedback?: { severity: 'success' | 'error'; text: string } | null;
  onClearPdfRegenerateFeedback?: () => void;
  onEdit: (row: StrapiQuotationRow) => void;
  onCopy: (row: StrapiQuotationRow) => void;
  onCreateWorkOrder: (row: StrapiQuotationRow) => void;
  onViewSamplingSchedules?: (row: StrapiQuotationRow) => void;
  onWorkflowUpdated?: () => void;
  accessCodes?: readonly string[] | null;
};

export function QuotationDataGrid({
  rows,
  rowCount,
  paginationModel,
  onPaginationModelChange,
  sortModel,
  onSortModelChange,
  isLoading,
  isError,
  error,
  user,
  jwt,
  onViewDetail,
  onPreview,
  onPreviewQuotationPdf,
  pdfRegenerateFeedback = null,
  onClearPdfRegenerateFeedback,
  onEdit,
  onCopy,
  onCreateWorkOrder,
  onViewSamplingSchedules,
  onWorkflowUpdated,
  accessCodes,
}: QuotationDataGridProps) {
  const [incompleteCustomerIds, setIncompleteCustomerIds] = useState<ReadonlySet<string>>(() => new Set());
  const onCustomerIncompleteChange = useCallback((documentId: string, incomplete: boolean) => {
    setIncompleteCustomerIds((prev) => {
      if (prev.has(documentId) === incomplete) return prev;
      const next = new Set(prev);
      if (incomplete) next.add(documentId);
      else next.delete(documentId);
      return next;
    });
  }, []);
  const columns: GridColDef<StrapiQuotationRow>[] = useMemo(
    () => [
      { field: 'quotation_number', headerName: 'No. quotation', minWidth: 130, flex: 0.5 },
      {
        field: 'request_date',
        headerName: 'Tanggal permintaan',
        minWidth: 140,
        flex: 0.4,
        valueFormatter: (v) => (v ? formatDate(String(v)) : '—'),
      },
      {
        field: 'customer',
        headerName: 'Pelanggan',
        minWidth: 180,
        flex: 1,
        valueGetter: (v) => (v as StrapiQuotationRow['customer'] | null | undefined)?.name ?? '',
      },
      {
        field: 'progress_status',
        headerName: 'Status',
        minWidth: 160,
        flex: 0.55,
        valueGetter: (_v, row) => quotationStatusDisplayLabel(row),
      },
      {
        field: 'sample_pickup_method',
        headerName: 'Metode ambil sampel',
        minWidth: 140,
        flex: 0.4,
        valueFormatter: (v) => (v === 'diantar' ? 'Diantar' : 'Sampling'),
      },
      {
        field: 'tahapan',
        headerName: 'Tahapan',
        minWidth: 170,
        flex: 0.55,
        sortable: false,
        valueGetter: (_v, row) => resolveQuotationTahapan(row),
      },
      {
        field: 'quotation_type',
        headerName: 'Type',
        minWidth: 150,
        flex: 0.4,
        sortable: false,
        valueGetter: (_v, row) => resolveQuotationTypeLabel(row),
      },
      {
        field: 'assignee_user',
        headerName: 'Penanggung jawab',
        minWidth: 140,
        flex: 0.45,
        sortable: false,
        valueGetter: (_v, row) => formatUserStub(row.assignee_user),
      },
      {
        field: 'updatedAt',
        headerName: 'Terakhir diubah',
        minWidth: 150,
        flex: 0.45,
        valueFormatter: (v) => (v ? formatDateTime(String(v)) : '—'),
      },
      {
        field: '__actions',
        headerName: 'Aksi',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        minWidth: 276,
        flex: 1,
        align: 'left',
        headerAlign: 'left',
        renderCell: ({ row }) => {
          const showEdit = canShowEditInGrid(row, user);
          const showCopy = canCopyQuotation(row, user);
          const showWo = canShowCreateWorkOrder(row, user);
          const showWorkflowUi = canShowQuotationWorkflowUi(row, user, accessCodes);
          const showPdf = canShowQuotationPdfPreview(row);
          return (
            <Box
              sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', py: 0.5 }}
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
            >
              <QuotationCustomerIncompleteHint
                row={row}
                user={user}
                jwt={jwt}
                onIncompleteChange={onCustomerIncompleteChange}
              />
              {showPdf ? (
                <Tooltip title="PDF Quotation">
                  <IconButton
                    size="small"
                    aria-label={`PDF Quotation ${row.quotation_number}`}
                    onClick={() => onPreviewQuotationPdf(row)}
                    data-testid={`quotation-action-pdf-${row.documentId}`}
                  >
                    <PictureAsPdfOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
              <Tooltip title="Preview quotation (dokumen lengkap)">
                <IconButton
                  size="small"
                  aria-label={`Preview quotation ${row.quotation_number}`}
                  onClick={() => onPreview(row)}
                  data-testid={`quotation-action-preview-${row.documentId}`}
                >
                  <ArticleOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Lihat detail">
                <IconButton
                  size="small"
                  aria-label={`Lihat quotation ${row.quotation_number}`}
                  onClick={() => onViewDetail(row)}
                  data-testid={`quotation-action-view-${row.documentId}`}
                >
                  <VisibilityOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {showEdit ? (
                <Tooltip title="Ubah quotation">
                  <IconButton
                    size="small"
                    aria-label={`Ubah quotation ${row.quotation_number}`}
                    onClick={() => onEdit(row)}
                    data-testid={`quotation-action-edit-${row.documentId}`}
                  >
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
              {showCopy ? (
                <Tooltip title="Copy quotation">
                  <IconButton
                    size="small"
                    aria-label={`Copy quotation ${row.quotation_number}`}
                    onClick={() => onCopy(row)}
                    data-testid={`quotation-action-copy-${row.documentId}`}
                  >
                    <ContentCopyOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
              {showWorkflowUi && jwt ? (
                <QuotationWorkflowActions
                  jwt={jwt}
                  row={row}
                  user={user}
                  variant="grid"
                  onCreateWorkOrder={showWo ? () => onCreateWorkOrder(row) : undefined}
                  onViewSamplingSchedules={
                    canShowViewSamplingSchedules(row, user, accessCodes)
                      ? () => onViewSamplingSchedules?.(row)
                      : undefined
                  }
                  onUpdated={onWorkflowUpdated}
                  accessCodes={accessCodes}
                />
              ) : null}
            </Box>
          );
        },
      },
    ],
    [accessCodes, jwt, onCopy, onCreateWorkOrder, onCustomerIncompleteChange, onViewSamplingSchedules, onEdit, onPreview, onPreviewQuotationPdf, onViewDetail, onWorkflowUpdated, user],
  );

  if (isError) {
    return <Alert severity="error">{error instanceof Error ? error.message : 'Gagal memuat quotation.'}</Alert>;
  }

  return (
    <Box sx={{ width: '100%' }} data-testid="quotations-data-grid">
      <Snackbar
        open={Boolean(pdfRegenerateFeedback)}
        autoHideDuration={6000}
        onClose={() => onClearPdfRegenerateFeedback?.()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {pdfRegenerateFeedback ? (
          <Alert
            severity={pdfRegenerateFeedback.severity}
            onClose={() => onClearPdfRegenerateFeedback?.()}
            sx={{ width: '100%' }}
          >
            {pdfRegenerateFeedback.text}
          </Alert>
        ) : undefined}
      </Snackbar>
      {isLoading && rows.length === 0 ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(r) => r.documentId}
          disableRowSelectionOnClick
          paginationMode="server"
          sortingMode="server"
          sortingOrder={['asc', 'desc']}
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={onPaginationModelChange}
          sortModel={sortModel}
          onSortModelChange={onSortModelChange}
          pageSizeOptions={[...DATA_GRID_NUMBERED_PAGE_SIZE_OPTIONS]}
          loading={isLoading}
          slots={{ pagination: DataGridNumberedPagination }}
          autoHeight
          density="compact"
          getRowClassName={(params) => {
            const classes: string[] = [];
            if (quotationIsNeedAttention(params.row)) classes.push('quotation-row-need-attention');
            if (incompleteCustomerIds.has(params.row.documentId)) {
              classes.push('quotation-row-customer-incomplete');
            }
            return classes.join(' ');
          }}
          sx={{
            '& .MuiDataGrid-columnHeaders': { fontWeight: 600 },
            '& .quotation-row-need-attention': {
              bgcolor: 'warning.light',
              '&:hover': { bgcolor: 'warning.light' },
            },
            '& .MuiDataGrid-row.quotation-row-customer-incomplete': {
              bgcolor: '#fff3bf',
              '& .MuiDataGrid-cell': { bgcolor: '#fff3bf' },
              '&:hover, &.Mui-hovered': {
                bgcolor: '#fff3bf',
                '& .MuiDataGrid-cell': { bgcolor: '#fff3bf' },
              },
            },
          }}
        />
      )}
    </Box>
  );
}
